import PropTypes from 'prop-types'

/**
 * Updates the `_path` and `_parentIds` of children in a tree structure recursively.
 * This function should return an tree object with the updated children.
 * @param {Object} options - The options for updating children.
 * @param {string} options._id - The ID of the parent node.
 * @param {string} options.oldId - The old ID to be replaced.
 * @param {Object} options.children - The children nodes to be updated.
 * @param {string} options.method - The method to be used for updating the children. Can be 'delete' or 'update'.
 * @returns {Promise<Object|null>} A promise that resolves to the updated children object or null if there are no children.
 */
const updateChildrenPathAndParentIds = async ({
  _id,
  oldId,
  children,
  method
}) => {
  return new Promise((resolve) => {
    const arrayOfPromises = Object.values(children || {}).map(async (child) => {
      // Create a deep copy of the 'child' object and assign it to 'newChild'
      const newChild = JSON.parse(JSON.stringify(child))

      if (method === 'delete') {
        // If the method is 'delete', remove the oldId in the _path and _parentIds
        newChild._path = newChild._path.replace(`${oldId}/children/`, '')
        newChild._parentIds = newChild?._parentIds?.filter(
          (parentId) => parentId !== oldId
        )
      } else if (method === 'update') {
        // If the method is 'update', replace the oldId with the _id in the _path and _parentIds
        newChild._path = child._path.replace(oldId, _id)
        newChild._parentIds = child?._parentIds?.map((parentId) =>
          parentId === oldId ? _id : parentId
        )
      }

      if (Object.keys(child.children || {})?.length) {
        const childrenUpdated = await updateChildrenPathAndParentIds({
          _id,
          oldId,
          children: child.children,
          method
        })

        newChild.children = childrenUpdated
      }

      return newChild
    })

    Promise.all(arrayOfPromises).then((results) => {
      resolve(
        results.reduce(
          (acc, element) => ({ ...acc, [element._id]: element }),
          {}
        ) || null
      )
    })
  })
}

updateChildrenPathAndParentIds.propTypes = {
  _id: PropTypes.string,
  oldId: PropTypes.string.isRequired,
  children: PropTypes.object,
  method: PropTypes.oneOf(['delete', 'update'])
}

export default updateChildrenPathAndParentIds
